// One edition of a training — "الطبعة N" — with everything that belongs to that
// run: its schedule and trainer (overview), its marketing report, its costs and
// pricing, its feedback form and the responses to it.
//
// Tabs follow the role: every role gets the overview (the schedule, as the old
// scheduling page gave them); the report needs canViewTrainings; the costs need
// canViewCostReports (sales edit them, admins read them); the form and
// its responses need canManageQuestionnaires, since responses carry
// respondents' names and phone numbers.

import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	redirect,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	ChevronLeft,
	ExternalLink,
	Loader2,
	Mail,
	MessageCircle,
	Pencil,
	Trash2,
} from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { CostReportPanel } from "#/components/admin/cost-report";
import { EditionQuestionnairePanel } from "#/components/admin/edition-questionnaire";
import {
	EditionDialog,
	EditionFollowUp,
	type EditionRow,
	EditionStatusBadge,
	ROW_ACTION,
	useEditionRemoval,
	whatsappHref,
} from "#/components/admin/editions";
import { MarketingReportPanel } from "#/components/admin/marketing-report";
import { GhostButton } from "#/components/admin/modal";
import { QuestionnaireResponses } from "#/components/admin/questionnaire-responses";
import { TabBar, type TabItem } from "#/components/admin/tabs";
import { authClient } from "#/lib/auth-client";
import { countdownLabel, editionTitle, isoDay } from "#/lib/editions";
import { algiersToday } from "#/lib/lead-filters";
import {
	canManageEditions,
	canManageQuestionnaires,
	canViewCostReports,
	canViewEditions,
	canViewTrainings,
	homeRouteFor,
} from "#/lib/roles";
import { getSession } from "#/lib/session";

const TAB_KEYS = [
	"overview",
	"report",
	"costs",
	"questionnaire",
	"responses",
] as const;
type EditionTab = (typeof TAB_KEYS)[number];

const searchSchema = z.object({ tab: z.enum(TAB_KEYS).optional() });

type EditionSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/editions/$editionId")({
	validateSearch: (search: Record<string, unknown>): EditionSearch => {
		const parsed = searchSchema.safeParse(search);
		return parsed.success ? parsed.data : {};
	},
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		if (!canViewEditions(session.user.role)) {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	component: EditionPage,
});

function EditionPage() {
	const params = Route.useParams();
	const editionId = Number(params.editionId);
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const { trpc } = useRouteContext({ from: "__root__" });
	const { data: session } = authClient.useSession();
	const role = session?.user?.role;
	const [editing, setEditing] = useState(false);
	const today = algiersToday();

	const editionsQuery = useQuery(trpc.editions.list.queryOptions());
	const { remove, isPending: removing } = useEditionRemoval((removed) => {
		if (canViewTrainings(role)) {
			void navigate({
				to: "/admin/trainings/$trainingId",
				params: { trainingId: removed.trainingId },
			});
		} else {
			void navigate({ to: "/admin/trainings" });
		}
	});

	if (editionsQuery.isLoading) {
		return (
			<div className="flex justify-center py-20">
				<Loader2 className="h-6 w-6 animate-spin text-muted" />
			</div>
		);
	}

	const edition = (editionsQuery.data as EditionRow[] | undefined)?.find(
		(e) => e.courseNumber === editionId,
	);
	if (!edition) {
		return (
			<div className="flex flex-col items-center gap-3 py-20 text-center">
				<p className="text-sm font-semibold text-red-600">الطبعة غير موجودة.</p>
				<Link
					to="/admin/trainings"
					className="text-sm font-bold text-teal-600 hover:underline"
				>
					العودة إلى التدريبات
				</Link>
			</div>
		);
	}

	const showReport = canViewTrainings(role);
	const showCosts = canViewCostReports(role);
	const showForm = canManageQuestionnaires(role);
	const tabs: TabItem<EditionTab>[] = [
		{ key: "overview", label: "نظرة عامة" },
		...(showReport ? [{ key: "report" as const, label: "تقرير التسويق" }] : []),
		...(showCosts
			? [{ key: "costs" as const, label: "التكاليف والتسعير" }]
			: []),
		...(showForm
			? [
					{ key: "questionnaire" as const, label: "استمارة التقييم" },
					{
						key: "responses" as const,
						label: "الإجابات",
						count: edition.questionnaire?._count.responses ?? 0,
					},
				]
			: []),
	];
	const tab: EditionTab =
		search.tab && tabs.some((t) => t.key === search.tab)
			? search.tab
			: "overview";

	function openTab(next: EditionTab) {
		void navigate({
			search: next === "overview" ? {} : { tab: next },
			replace: true,
		});
	}

	return (
		<div className="flex flex-col gap-5">
			<header className="flex flex-col gap-3 print:hidden">
				<nav className="flex flex-wrap items-center gap-1 text-xs font-bold text-muted">
					<Link to="/admin/trainings" className="hover:text-navy">
						التدريبات
					</Link>
					{showReport ? (
						<>
							<ChevronLeft className="h-3.5 w-3.5" />
							<Link
								to="/admin/trainings/$trainingId"
								params={{ trainingId: edition.trainingId }}
								className="hover:text-navy"
							>
								{edition.training.name}
							</Link>
						</>
					) : null}
				</nav>

				<div className="flex flex-wrap items-start justify-between gap-4">
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-2">
							<h1 className="text-2xl font-bold text-navy">
								{editionTitle(edition.training.name, edition.editionNumber)}
							</h1>
							<EditionStatusBadge edition={edition} today={today} />
						</div>
						<p className="mt-1 text-sm text-muted">
							#{edition.courseNumber} ·{" "}
							<bdi dir="ltr">
								{isoDay(edition.startDate)} → {isoDay(edition.endDate)}
							</bdi>{" "}
							· {edition.trainerName}
						</p>
					</div>
					{canManageEditions(role) ? (
						<div className="flex flex-wrap items-center gap-2">
							<GhostButton onClick={() => setEditing(true)}>
								<Pencil className="h-4 w-4" />
								تعديل الطبعة
							</GhostButton>
							<button
								type="button"
								onClick={() => remove(edition)}
								disabled={removing}
								className="inline-flex items-center justify-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-50 disabled:pointer-events-none disabled:opacity-50"
							>
								<Trash2 className="h-4 w-4" />
								حذف
							</button>
						</div>
					) : null}
				</div>
			</header>

			{tabs.length > 1 ? (
				<TabBar tabs={tabs} value={tab} onChange={openTab} />
			) : null}

			{tab === "overview" ? (
				<EditionOverview
					edition={edition}
					today={today}
					showFollowUp={showReport && showForm}
					showFormLink={showForm}
					onOpenTab={openTab}
				/>
			) : null}

			{tab === "report" ? (
				<MarketingReportPanel editionId={edition.courseNumber} />
			) : null}

			{tab === "costs" ? (
				<CostReportPanel editionId={edition.courseNumber} />
			) : null}

			{tab === "questionnaire" ? (
				<EditionQuestionnairePanel edition={edition} />
			) : null}

			{tab === "responses" ? (
				edition.questionnaire ? (
					<QuestionnaireResponses
						editionId={edition.courseNumber}
						title={editionTitle(edition.training.name, edition.editionNumber)}
					/>
				) : (
					<div className="rounded-2xl border border-dashed border-line bg-white px-6 py-14 text-center">
						<p className="font-bold text-navy">
							لا توجد استمارة تقييم لهذه الطبعة بعد.
						</p>
						<button
							type="button"
							onClick={() => openTab("questionnaire")}
							className="mt-2 text-sm font-bold text-teal-600 hover:underline"
						>
							إنشاء الاستمارة
						</button>
					</div>
				)
			) : null}

			{editing ? (
				<EditionDialog edition={edition} onClose={() => setEditing(false)} />
			) : null}
		</div>
	);
}

function EditionOverview({
	edition,
	today,
	showFollowUp,
	showFormLink,
	onOpenTab,
}: {
	edition: EditionRow;
	today: string;
	showFollowUp: boolean;
	showFormLink: boolean;
	onOpenTab: (tab: EditionTab) => void;
}) {
	const sessionDays = edition.sessionDates.map(isoDay).sort();
	const startDay = isoDay(edition.startDate);
	const endDay = isoDay(edition.endDate);
	const feedbackPath = `/feedback/${edition.training.slug}/${edition.editionNumber}`;

	return (
		<div className="grid gap-4 lg:grid-cols-2">
			<Card title="المدرب">
				<p className="font-semibold text-ink">{edition.trainerName}</p>
				<div className="flex flex-wrap items-center gap-3">
					{edition.trainerPhone ? (
						<a
							href={whatsappHref(edition.trainerPhone)}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1.5 text-xs font-bold text-teal-600 transition-colors hover:bg-teal-100"
						>
							<MessageCircle className="h-3.5 w-3.5" />
							واتساب <bdi dir="ltr">{edition.trainerPhone}</bdi>
						</a>
					) : null}
					{edition.trainerEmail ? (
						<a
							href={`mailto:${edition.trainerEmail}`}
							className="inline-flex items-center gap-1.5 text-sm text-navy hover:underline"
						>
							<Mail className="h-3.5 w-3.5" />
							{edition.trainerEmail}
						</a>
					) : null}
					{!edition.trainerPhone && !edition.trainerEmail ? (
						<span className="text-sm text-muted">لا توجد معلومات تواصل.</span>
					) : null}
				</div>
			</Card>

			<Card title="الجدول">
				<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
					<Detail label="الفترة">
						<bdi dir="ltr">
							{startDay} → {endDay}
						</bdi>
					</Detail>
					<Detail label="متبقٍ لأول يوم">
						{countdownLabel(sessionDays, startDay, endDay, today)}
					</Detail>
					<Detail label="التوقيت">{edition.timeSlot || "—"}</Detail>
					<Detail label="إجمالي الساعات">{edition.totalHours ?? "—"}</Detail>
				</dl>
				<div>
					<p className="mb-1.5 text-xs font-bold text-muted">الأيام المتقطعة</p>
					{sessionDays.length ? (
						<div dir="ltr" className="flex flex-wrap gap-1">
							{sessionDays.map((day) => (
								<span
									key={day}
									className="rounded-md bg-teal-50 px-1.5 py-0.5 text-xs font-semibold text-teal-600"
								>
									{day}
								</span>
							))}
						</div>
					) : (
						<span className="text-sm text-muted">لم تُحدَّد بعد.</span>
					)}
				</div>
			</Card>

			<Card title="الروابط">
				<dl className="flex flex-col gap-2 text-sm">
					<Detail label="صفحة الهبوط">
						{/* An inactive training has no public page to link to. */}
						{edition.training.isActive ? (
							<ExternalAnchor href={`/training/${edition.training.slug}`}>
								/training/{edition.training.slug}
							</ExternalAnchor>
						) : (
							<span className="text-muted">التدريب معطّل — لا صفحة منشورة</span>
						)}
					</Detail>
					<Detail label="البطاقة التقنية (FT)">
						{edition.ftUrl ? (
							<ExternalAnchor href={edition.ftUrl}>فتح</ExternalAnchor>
						) : (
							"—"
						)}
					</Detail>
					<Detail label="الطلبات المؤكدة">
						<Link
							to="/admin/leads"
							search={{
								trainingId: edition.trainingId,
								editions: [String(edition.courseNumber)],
							}}
							className="font-semibold text-teal-600 hover:underline"
						>
							{edition._count.leads} طلب — عرضها في الطلبات
						</Link>
					</Detail>
					{showFormLink ? (
						<Detail label="استمارة التقييم">
							{edition.questionnaire ? (
								<ExternalAnchor href={feedbackPath}>
									{feedbackPath}
								</ExternalAnchor>
							) : (
								<span className="text-muted">لم تُنشأ بعد</span>
							)}
						</Detail>
					) : null}
				</dl>
			</Card>

			<Card title="ملاحظة التواصل">
				<p className="whitespace-pre-line text-sm text-ink">
					{edition.contactNote || <span className="text-muted">—</span>}
				</p>
			</Card>

			{showFollowUp ? (
				<Card title="المتابعة" className="lg:col-span-2">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<EditionFollowUp edition={edition} />
						<div className="flex flex-wrap items-center gap-1.5">
							<button
								type="button"
								onClick={() => onOpenTab("report")}
								className={ROW_ACTION}
							>
								تقرير التسويق
							</button>
							<button
								type="button"
								onClick={() => onOpenTab("costs")}
								className={ROW_ACTION}
							>
								التكاليف والتسعير
							</button>
							<button
								type="button"
								onClick={() => onOpenTab("questionnaire")}
								className={ROW_ACTION}
							>
								استمارة التقييم
							</button>
							<button
								type="button"
								onClick={() => onOpenTab("responses")}
								className={ROW_ACTION}
							>
								الإجابات
							</button>
						</div>
					</div>
				</Card>
			) : null}
		</div>
	);
}

function Card({
	title,
	className = "",
	children,
}: {
	title: string;
	className?: string;
	children: React.ReactNode;
}) {
	return (
		<section
			className={`flex flex-col gap-3 rounded-2xl border border-line bg-white p-5 shadow-card ${className}`}
		>
			<h2 className="font-display text-base font-bold text-navy">{title}</h2>
			{children}
		</section>
	);
}

function Detail({
	label,
	children,
}: {
	label: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-0.5">
			<dt className="text-xs font-bold text-muted">{label}</dt>
			<dd className="text-ink">{children}</dd>
		</div>
	);
}

function ExternalAnchor({
	href,
	children,
}: {
	href: string;
	children: React.ReactNode;
}) {
	return (
		<a
			href={href}
			target="_blank"
			rel="noreferrer"
			className="inline-flex items-center gap-1 font-semibold text-teal-600 hover:underline"
			dir="ltr"
		>
			{children}
			<ExternalLink className="h-3 w-3" />
		</a>
	);
}
